import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy, Lock, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import type { GuestWifiNetwork } from '@hotel-voice/shared';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { ErrorState, ListSkeleton, StateView, UnavailableState } from '@/components/common/StateView';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useReadyGuest } from '@/contexts/GuestSessionContext';
import { copyToClipboard } from './clipboard';

/**
 * Wi-Fi Details: one card per network the guest's room is on.
 *
 * The password is printed in the clear with a copy button beside it — the
 * whole point of the page is to get it into the phone's Wi-Fi settings, and a
 * masked field with an eye icon is one more tap between a tired guest and the
 * internet. The server already filtered the list to this room, so there is
 * nothing here to choose; the two empty states are told apart by
 * `anyConfigured`.
 */
export function WifiPage() {
  const { session, services, servicesPhase, servicesError, reloadServices } = useReadyGuest();

  const body = () => {
    if (servicesPhase === 'loading' || servicesPhase === 'idle') return <ListSkeleton rows={2} />;
    if (servicesPhase === 'error') {
      return <ErrorState message={servicesError ?? 'Could not load Wi-Fi details.'} onRetry={reloadServices} />;
    }
    if (!services?.flags.wifiService) return <UnavailableState service="Wi-Fi details" />;

    const { networks, anyConfigured } = services.wifi;

    if (networks.length === 0) {
      return (
        <StateView
          icon={WifiOff}
          title={anyConfigured ? 'No Wi-Fi for your room' : 'No Wi-Fi networks available'}
          message={
            anyConfigured
              ? 'No Wi-Fi network is available for your room. Please contact hotel reception for assistance.'
              : 'No Wi-Fi networks are currently available. Please contact hotel reception for assistance.'
          }
        >
          <Button asChild>
            <Link to="/call">Call Reception</Link>
          </Button>
          <Button asChild variant="ghost">
            <Link to="/">Back to home</Link>
          </Button>
        </StateView>
      );
    }

    return (
      <div className="flex flex-col gap-4 p-4">
        {networks.map((network) => (
          <WifiCard key={network.id} network={network} />
        ))}
        <p className="text-muted-foreground px-1 text-xs">
          Open your phone’s Wi-Fi settings, choose the network and paste the password.
        </p>
      </div>
    );
  };

  return (
    <AppShell>
      <PageHeader title="Wi-Fi Details" subtitle={`Room ${session.roomNumber}`} />
      {body()}
    </AppShell>
  );
}

function WifiCard({ network }: { network: GuestWifiNetwork }) {
  const open = network.securityType === 'Open';
  return (
    <Card>
      <CardHeader className="flex-row items-center gap-3">
        <span className="bg-tile-blue flex size-11 shrink-0 items-center justify-center rounded-xl text-blue-600">
          <Wifi className="size-5" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <CardTitle className="truncate text-base">{network.ssid}</CardTitle>
          <Badge variant="secondary" className="mt-1 gap-1">
            {!open && <Lock />}
            {network.securityType}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <CopyRow label="Network" value={network.ssid} />
        {open ? (
          <div className="bg-muted rounded-xl px-3 py-2.5 text-sm">
            <span className="text-muted-foreground">Password: </span>No password needed
          </div>
        ) : (
          <CopyRow label="Password" value={network.password} mono />
        )}
        {network.additionalInfo && (
          <p className="text-muted-foreground text-sm leading-snug">{network.additionalInfo}</p>
        )}
      </CardContent>
    </Card>
  );
}

/** A labelled value with a copy button; the button flips to a tick for a moment on success. */
function CopyRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const ok = await copyToClipboard(value);
    if (!ok) {
      toast.error('Could not copy — please type it in.');
      return;
    }
    setCopied(true);
    toast.success(`${label} copied`);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="bg-muted flex items-center gap-2 rounded-xl py-1.5 pr-1.5 pl-3">
      <div className="min-w-0 flex-1">
        <p className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">{label}</p>
        <p className={`truncate text-sm font-semibold select-all ${mono ? 'font-mono' : ''}`}>{value}</p>
      </div>
      <Button
        variant="outline"
        size="icon-sm"
        aria-label={`Copy ${label.toLowerCase()}`}
        onClick={() => void handleCopy()}
        className="shrink-0"
      >
        {copied ? <Check className="text-success" /> : <Copy />}
      </Button>
    </div>
  );
}
